import { contentHash, splitName, type CloudLink, type DocMeta, type LineEnding, type TextFormat } from './documents';

/**
 * Pure rules for cloud documents: when the cloud is behind, how a revision is
 * recorded, what a conflict copy is called. No network, no DOM, no React.
 *
 * Three things are kept apart, deliberately:
 * - local persistence (every edit, immediately, in this browser) — not modelled here;
 * - cloud synchronisation: `DocMeta.cloud` + `unsynced`;
 * - files on the device: `DocMeta.dirty`, `origin`, `savedHash`.
 */

export const sameFormat = (a: TextFormat, b: TextFormat) => a.eol === b.eol && a.bom === b.bom;

export const eolToWire = (eol: LineEnding): 'lf' | 'crlf' => (eol === '\r\n' ? 'crlf' : 'lf');
export const eolFromWire = (eol: string): LineEnding => (eol === 'crlf' ? '\r\n' : '\n');

export type CloudMeta = DocMeta & { cloud: CloudLink };

export const isCloudDoc = (meta: DocMeta): meta is CloudMeta => !!meta.cloud;

/**
 * A document whose only home besides this browser is the cloud. Closing it
 * never asks about files; its protection is the cloud, or — until the cloud has
 * it — the local copy that survives closing.
 */
export const isCloudOnly = (meta: DocMeta): boolean => !!meta.cloud && meta.origin !== 'file';

/**
 * Whether the document has words that exist nowhere safer than this browser.
 * For a cloud-only document that is "not yet in the cloud"; otherwise "not yet
 * in its file".
 */
export function isUnprotected(meta: DocMeta): boolean {
  return isCloudOnly(meta) ? !!meta.unsynced : meta.dirty;
}

/**
 * The cloud is behind the editor. A brand-new link (`revision: 0`) waits for
 * the first word: an untouched "Untitled" is never created in the cloud.
 */
export function needsPush(meta: DocMeta, text: string, workspaceId: string | null): boolean {
  const link = meta.cloud;
  if (!link) return false;
  if (link.revision === 0) return text !== '';
  return (
    contentHash(text) !== link.hash ||
    meta.name !== link.name ||
    meta.kind !== link.kind ||
    !sameFormat(meta.format, link.format) ||
    workspaceId !== link.workspaceId
  );
}

/** The link after the server acknowledged `revision` for exactly this pushed state. */
export function acknowledged(account: string, revision: number, pushed: { text: string; name: string; kind: DocMeta['kind']; format: TextFormat; workspaceId: string | null }): CloudLink {
  return { account, revision, hash: contentHash(pushed.text), name: pushed.name, kind: pushed.kind, format: { ...pushed.format }, workspaceId: pushed.workspaceId };
}

export interface RemoteSnapshot {
  id: string;
  name: string;
  kind: DocMeta['kind'];
  content: string;
  eol: string;
  has_bom: boolean;
  workspace_id: string | null;
  revision: number;
}

/** A link describing exactly what the server holds, from a row the server returned. */
export function linkFromRemote(account: string, remote: RemoteSnapshot): CloudLink {
  return {
    account,
    revision: remote.revision,
    hash: contentHash(remote.content),
    name: remote.name,
    kind: remote.kind,
    format: { eol: eolFromWire(remote.eol), bom: remote.has_bom },
    workspaceId: remote.workspace_id,
  };
}

const pad = (n: number) => String(n).padStart(2, '0');

/** "Notes.md" → "Notes (conflict copy 2026-10-08 14.32).md". No characters a file name cannot hold. */
export function conflictCopyName(name: string, when: Date, existing: readonly string[] = []): string {
  const { base, ext } = splitName(name);
  const stamp = `${when.getFullYear()}-${pad(when.getMonth() + 1)}-${pad(when.getDate())} ${pad(when.getHours())}.${pad(when.getMinutes())}`;
  const trimmed = base.replace(/ \(conflict copy [^)]*\)$/, '').slice(0, 200);
  const taken = new Set(existing.map((n) => n.toLowerCase()));
  let candidate = `${trimmed} (conflict copy ${stamp})${ext}`;
  for (let n = 2; taken.has(candidate.toLowerCase()); n++) candidate = `${trimmed} (conflict copy ${stamp} ${n})${ext}`;
  return candidate;
}

/** What the person is told about one document's relationship with the cloud. */
export type CloudDocState =
  | 'local' // not in a cloud library
  | 'synced' // the server acknowledged exactly this text
  | 'syncing' // a write is in flight
  | 'pending' // changes are waiting for the next sync (autosave batching or autosave off)
  | 'offline' // waiting for the network
  | 'signed-out' // belongs to an account that is not signed in
  | 'blocked' // cannot sync until the person acts (sign in again, no access, rejected content)
  | 'error'; // failed; will retry

/** The roll-up shown in one place. */
export type CloudSummary =
  | { state: 'off' } // cloud not configured in this build
  | { state: 'signed-out' }
  | { state: 'expired' }
  | { state: 'denied' }
  | { state: 'synced'; at: number | null }
  | { state: 'syncing' }
  | { state: 'pending'; count: number }
  | { state: 'offline'; count: number }
  | { state: 'error'; count: number; message: string };

/**
 * The name, format and encoding a document takes when the server's version
 * replaces it. With `keepLocal`, anything changed on this device since the last
 * acknowledged revision (`was`) stays as edited, and the rest follows the server.
 */
export function adoptedFields(
  was: CloudLink,
  local: { name: string; kind: DocMeta['kind']; format: TextFormat },
  remote: RemoteSnapshot,
  keepLocal: boolean,
): { name: string; kind: DocMeta['kind']; format: TextFormat } {
  const theirs = { name: remote.name, kind: remote.kind, format: { eol: eolFromWire(remote.eol), bom: remote.has_bom } };
  if (!keepLocal) return theirs;
  return {
    name: local.name !== was.name ? local.name : theirs.name,
    kind: local.kind !== was.kind ? local.kind : theirs.kind,
    format: !sameFormat(local.format, was.format) ? local.format : theirs.format,
  };
}
